import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { extractError } from "@/api/client";
import useIdleTimeout from "@/hooks/useIdleTimeout";
import IdleWarningOverlay from "@/components/IdleWarningOverlay";

const AuthContext = createContext(null);

// Idle-timeout durations (ms) per role. Roles omitted from this map have NO auto-logout
// (driver + restaurant stay logged in indefinitely — their work sessions can be long).
const IDLE_TIMEOUT_MS = {
  admin: 5 * 60 * 1000,        // 5 minutes
  customer: 30 * 60 * 1000,    // 30 minutes
};
const IDLE_WARNING_MS = 30 * 1000;  // 30-second countdown before actual logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      localStorage.setItem("talabya_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // Customer signups return {pending: true} — no token, no user session.
      if (data?.pending) {
        return { ok: true, pending: true, identifier: data.identifier, message: data.message };
      }
      localStorage.setItem("talabya_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("talabya_token");
    setUser(null);
  };

  const idleLogout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("talabya_token");
    setUser(null);
    toast.error("تم تسجيل خروجك تلقائياً بسبب عدم النشاط");
    nav("/login", { replace: true });
  }, [nav]);

  const idleTimeoutMs = user ? (IDLE_TIMEOUT_MS[user.role] ?? IDLE_TIMEOUT_MS.customer) : 0;
  const { warningSecondsLeft, dismissWarning } = useIdleTimeout({
    timeoutMs: idleTimeoutMs,
    warningMs: IDLE_WARNING_MS,
    onTimeout: idleLogout,
    enabled: !!user,
  });

  // Called by the ForgotPasswordModal after a successful /auth/reset-password — the endpoint
  // returns a fresh token so the user is transparently logged in without a second round-trip.
  const setSessionFromReset = ({ token, user }) => {
    if (token) localStorage.setItem("talabya_token", token);
    if (user) setUser(user);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, setSessionFromReset }}>
      {children}
      <IdleWarningOverlay secondsLeft={warningSecondsLeft} onStay={dismissWarning}/>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
